/**
 * Admin page for managing Resellers
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Checkbox, FormControl, FormControlLabel, InputLabel, Select, MenuItem, Grid, Paper,
  Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography
} from '@mui/material';

import Add from '@mui/icons-material/Add';
import Apps from '@mui/icons-material/Apps';
import Contacts from '@mui/icons-material/Contacts';
import CreditCard from '@mui/icons-material/CreditCard';
import Edit from '@mui/icons-material/Edit';
import MonetizationOn from '@mui/icons-material/MonetizationOn';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';

import AccountLedgerSummaryFetch from './AccountLedgerSummaryFetch';
import FundingSourceList from '../paymentsources/FundingSourceList';
import CurrencyTextField from '../ui/CurrencyTextField';
import NullablePercentageTextField from '../ui/NullablePercentageTextField';
import PercentageTextField from '../ui/PercentageTextField';
import ResellerFeeUpdateMutation from './ResellerFeeUpdateMutation';
import AccountUpdateMutation from './AccountUpdateMutation';
import Charges from './Charges';
import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
import SaveButton from '../ui/SaveButton';
import PhoneNumberFormat from '../ui/PhoneNumberFormat';
import Address from '../ui/Address'
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import ConfirmationDialog from '../ui/ConfirmationDialog';

import { red, amber } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();
const ResellerLookupQuery = graphql`
query ResellerLookupQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    parentId
    accountType
    accountNum
    accountName
    description
    accountStatus
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    stripeCid
    dwollaCid
    activecampaignId
    achActive
    accountStatus
    billingDayOfMonth
    maxCharge
    floatLimit
    floatBalance
    useBudgets
    mccAuth
    mccWhitelist
    feeMonthly
    feeCustPercentage
    feeSelfPercentage
    plaidCheckPercAllow
    achLimitNopeek
    achLimitAbsolute
    achPercAvailableOnCharge
    achMinAvailableOnCharge
    ccPercAvailableOnCharge
    ccMinAvailableOnCharge
    walletTsByAccountId {
      totalCount
    }
    feePlanTByFeePlanId {
      feePlanName
      feeMonthly
      feeCustPercentage
      feeSelfPercentage
    }
    accountTByParentId {
      appTsByAccountId {
        nodes {
          appId
          appName
        }
      }
    }
    accountTsByParentId {
      totalCount
    }
    paymentSourceTsByAccountId {
      totalCount
      nodes {
        psId
        psOrder
        extSource
        extPsId
        psType
        psStatus
      }
    }
    bindCardTsByProvisioningAccountId {
      totalCount
    }
    customerAppTsByCustomerAccountId {
      nodes {
        customerAppId
        parentAppId
        appId
        bcId
        appStatus
        bindCardTByBcId {
          extSource
          extBcId
          bcStatus
          firstName
          lastName
          address1
          city
          state
          country
          postalCode  
        }
        atId
        assetTypeTByAtId {
          atCode
          atSymbol
          placement
        }
        appSkuPrice
        appMarkupAbsolute
        appMarkupPercentage
        appStatus
        notes
        paymentOrder
        marqetaMid
        appTByAppId {
          parentAppId
          oemAppId
          appUrl
          appLogo
          appName
          appSku
          appType
        }
      }
    }
    userTsByAccountId(first: 20) {
      totalCount
      nodes {
        userId
        userName
        userRole
        lastName
        firstName
        email
        phone
        address1
        address2
        city
        state
        country
        postalCode
        marqetaUid
        emailVerified
      }
    }
  }
}
`;

const styleSheet = theme => ({
  tabroot: {
    flexGrow: 1,
    margin: 0,
    width: '100%',
  },
  flex: {
    flex: 1,
  },
  paper: {
    padding: theme.spacing(1),
    textAlign: 'left',
    // color: theme.palette.text.secondary,
    maxWidth: 980,
    marginTop: 16,
  },
  paper4: {
    //padding: theme.spacing(3),
    textAlign: 'left',
    // color: theme.palette.text.secondary,
    maxWidth: 980,
    marginTop: 24,
  },
  alsPaper: {
    padding: theme.spacing(1),
    // width: 200
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusdiv: {
    display: 'flex',
    alignItems: 'center',
    direction: 'row',
    justify: 'center',
  },
  button: {
    marginTop: 11,
    marginLeft: 12,
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 8,
    paddingRight: 8,
    //height: 16,
  },
  statusicon: {
    width: 52,
    height: 52,
    color: 'green',
  },
  erroricon: {
    width: 52,
    height: 52,
    color: 'red',
  },
  iconbutton: {
    height: 34,
    marginRight: 0,
  },
  mbutton: {
    width: 94,
    height: 34,
    marginRight: 0,
  },
  vbutton: {
    width: 130,
    height: 34,
    marginRight: 0,
  },
  addicon: {
    width: 24,
    height: 24,
  },
  nselect: {
    paddingLeft: 0,
  },
  editicon: {
    width: 22,
    height: 22,
  },
  percentageTextField: {
    marginLeft: 0,
    width: 160,
    minWidth: 160,
    textAlign: 'right',
    paddingLeft: 0
  },
  monthlyFeeTextField: {
    width: 160,
    minWidth: 160,    
  },
  percentField: {
    marginRight: theme.spacing(1),
    width: 135,
  }
});

function TabContainer(props) {
  return <div style={{padding:20}}>{props.children}</div>;
}

class Reseller extends Component {

  constructor(props) {
    super(props);

    const params = new URLSearchParams(this.props.location.search);
    let theTab = params.get('tab');

    this.state = {
      value: theTab == null ? 0: theTab,
      showSpinner: false,
      showTopupSpinner: false,
      showActivateSpinner: false,
      showSuspendSpinner: false,
      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,
      accountId: null,
      accountName: null,
      accountNum: null,
      description: null,
      accountStatus: null,
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      address1: null,
      address2: null,
      city: null,
      state: null,
      country: null,
      postalCode: null,
      stripeCid: null,
      dwollaCid: null,
      billingDayOfMonth: null,
      achActive: null, 
      useBudgets: null,
      maxCharge: null,
      floatLimit: null,
      payout: 0.00,
      acctCredit: 0.00,
      creditNotes: 'Account credit',

      alsAssetBalance: null,
      alsPayInAr: null,
      alsAssetAndFullArBalance: null,
      alsPayInArAvailable: null,
      alsAssetAndArBalance: null,
      alsAssetArAuthBalance: null,
      alsBalanceForTopupCalc: null,
      alsLiabilityBalance: null,
      alsEquityBalance: null,
      alsMaxPayoutBalance: null,
      alsAuthBalance: null,
      alsFloatBalanceDisplay: null,
      alsFloatBalanceForAuth: null,

      custApps: [],
      coverFees: true,
      mccAuth: null,
      mccWhitelist: null,
      feeMonthly: null,
      feeCustPercentage: null,
      feeSelfPercentage: null,
      verifyUserId: null,
      verifyConfirm: false,
      paymentRailsStatus: null,
      plaidCheckPercAllow: null,
      achLimitNopeek: null,
      achLimitAbsolute: null,
      achPercAvailableOnCharge: null,
      achMinAvailableOnCharge: null,
      ccPercAvailableOnCharge: null,
      ccMinAvailableOnCharge: null
    }
  }

  handleChange = event => {
    this.setState({ name: event.target.value });
  };

  handleTabChange = (event, value) => {
    this.setState({ value });
  }

  setFeeMonthly(value) {
    if (!value || value === '') {
      this.setState({feeMonthly: null});
    } else {
      this.setState({feeMonthly: value});
    }
  }

  setFeeCustPercentage(value, props) {
    if (!value || value === '') {
      this.setState({feeCustPercentage: null});
    } else {
      this.setState({feeCustPercentage: value});
    }
  }

  setFeeSelfPercentage(value, props) {
    if (!value || value === '') {
      this.setState({feeSelfPercentage: null});
    } else {
      this.setState({feeSelfPercentage: value});
    }
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  initiatePayout(accountId) {
    const { callPartnerApi } = this.props.auth;

    this.setState({saveStatus: 'saving'});

    let aPjwt = abEnvironment.getPjwt();
    let atId  = 'f5c70f86-135a-11e8-bb45-97ef56920c02';

    let payload = {
      operation: "initiatePayout",
      params: {
        pJwt: aPjwt,
        accountId: accountId,
        atId: atId,
        payoutAmount: this.state.payout,
        coverFees: this.state.coverFees
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      let abpResponse = response.data;
      if (abpResponse == null || abpResponse.status !== 0) {
        let theErrorText = "Unidentified Error";

        if (abpResponse != null && abpResponse.message != null) {
          theErrorText = abpResponse.message;
        }
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
        return;
      }

      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Payout sent', saveSnackbarDuration: 3000 });
    })
    .catch(error => {
      let theErrorText = "Unidentified Error";

      let abpResponse = error.data;
      if (abpResponse != null && abpResponse.message != null) {
        theErrorText = abpResponse.message;
      }
      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
    });
  }

  initiateCredit(accountId) {
    const { callPartnerApi } = this.props.auth;

    this.setState({saveStatus: 'saving'});

    let aPjwt = abEnvironment.getPjwt();
    // asset type USD.
    let atId  = 'f5c70f86-135a-11e8-bb45-97ef56920c02';

    let payload = {
      operation: "initiateCredit",
      params: {
        pJwt: aPjwt,
        accountId: accountId,
        atId: atId,
        creditAmount: this.state.acctCredit,
        notes: this.state.creditNotes
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      let abpResponse = response.data;
      if (abpResponse == null || abpResponse.status !== 0) {
        let theErrorText = "Unidentified Error";

        if (abpResponse != null && abpResponse.message != null) {
          theErrorText = abpResponse.message;
        }
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
        return;
      }

      //text for credit/debit change depending on positive/negative.
      let creditDebitText = Number(this.state.acctCredit) < 0 ? 'Debit' : 'Credit';
      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: creditDebitText + ' applied', saveSnackbarDuration: 3000 });
    })
    .catch(error => {
      let theErrorText = "Unidentified Error";

      let abpResponse = error.data;
      if (abpResponse != null && abpResponse.message != null) {
        theErrorText = abpResponse.message;
      }
      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
    });
  }

  goToUserCreateNonPrimary(route, propsToCache) {
    //here it is not the primary user, just pass the company address.
    let param = {
      'address1': propsToCache.address1,
      'address2': propsToCache.address2,
      'city': propsToCache.city,
      'state': propsToCache.state,
      'country': propsToCache.country,
      'postalCode': propsToCache.postalCode

    }
    localStorage.setItem('userCreateParamProps', JSON.stringify(param));
    this.props.history.push(`/${route}`);
  }

  goToUserCreate(route, propsToCache) {
    localStorage.setItem('userCreateParamProps', JSON.stringify(propsToCache));
    this.props.history.push(`/${route}`);
  }

  masqueradeAs(u_id, a_id, role, userName) {
    this.props.auth.masqueradeAs(u_id, a_id, role, userName);
  }

  
  confirmVerify = (userId) => {
    this.setState({verifyUserId: userId, verifyConfirm: true});
  }

    
  resendVerifyEmail = () => {

    const { callPartnerApi, logError } = this.props.auth;

 
    let payload = {
      operation: "resendVerifyEmail",
      params: {
        appBindUserId: this.state.verifyUserId
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      // console.log(JSON.stringify(response));
      let abpResponse = response.data;
      if (abpResponse == null || abpResponse.user == null) {
        let theErrorText = "Unidentified Error";

        if (abpResponse != null && abpResponse.message != null) {
          theErrorText = abpResponse.message;
        }
        logError('Error in resending verificatioh email: ' + theErrorText);
        this.setState({ verifyUserId: null, verifyConfirm: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Problem sending email', saveSnackbarDuration: null });
        return;
      }

      this.setState({ verifyUserId: null, verifyConfirm: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Email sent', saveSnackbarDuration: 3000 });
    })
    .catch(error => {
      let theErrorText = "Unidentified Error";

      let abpResponse = error.data;
      if (abpResponse != null && abpResponse.message != null) {
        theErrorText = abpResponse.message;
      }
      this.setState({ verifyUserId: null, verifyConfirm: false, saveSnackbarOpen: true, saveStatus: 'Problem sending email', saveMessage: theErrorText, saveSnackbarDuration: null });
    });

  }

  getValueForRender(state_member, prop_member, theDefault)
  {
    if (state_member != null)
      return state_member;
    
    if (prop_member != null)
      return prop_member;

    return theDefault;
  }

  disablePayoutButton() {
    let payoutVal = Number(this.state.payout);
    let maxPayoutVal = Number(this.state.alsMaxPayoutBalance);
    if (payoutVal <= 0 || payoutVal > maxPayoutVal) {
      return true;
    }
    return false;
  }

  disableCreditButton() {
    let creditVal = Number(this.state.acctCredit);
    if (creditVal === 0) {
      return true;
    }
    return false;
  }

  disableMCCWhitelist(accountProps) {

    const regex = new RegExp('^[0-9]*$');

    if (accountProps.mccWhitelist) {
      if (this.state.mccWhitelist === '') {
        //user blanks out previous value, all good
        return false;
      }
    } 
    
    //if user entered a value, make it conforms to NNNN or NNNN,NNNN, etc.  Cannot end in a comma
    if (this.state.mccWhitelist && this.state.mccWhitelist.length > 0) {
      if (this.state.mccWhitelist.length === 4) {
        //input exactly 4, test that it is all numbers
        if (regex.test(this.state.mccWhitelist)) {
          return false;
        } else {
          return true;
        }
      } else if (this.state.mccWhitelist.length > 4) {
        //split the comma seperated list and test each chunk
        const mccCodes = this.state.mccWhitelist.split(",");
        let i = 0;
        for (i = 0; i < mccCodes.length; i++) {
          let mccCode = mccCodes[i];
          if (mccCode.length === 4) {
            if (regex.test(mccCode)) {
              //this one is good, fall to next
            } else {
              //if any not good, disable.
              return true;
            }
    
          } else {
            return true;
          }
        }
        return false;
      } else {

        return true;
      }
    } else {
      return false;
    }

  }

  disableAccountSave(accountProps) {
    if (accountProps === null)
      return true;

    if (this.disableMCCWhitelist(accountProps)) {
      return true;
    }

    let retval = this.disableAttributeCheck(this.state.accountName, accountProps.accountName) &&
                 this.disableAttributeCheck(this.state.accountNum,  accountProps.accountNum) &&
                 this.disableAttributeCheck(this.state.description, accountProps.description) &&
                 this.disableAttributeCheck(this.state.firstName,   accountProps.contactFirstName) &&
                 this.disableAttributeCheck(this.state.lastName,    accountProps.contactLastName) &&
                 this.disableAttributeCheck(this.state.email,       accountProps.contactEmail) &&
                 this.disableAttributeCheck(this.state.phone,       accountProps.contactPhone) &&
                 this.disableAttributeCheck(this.state.address1,    accountProps.address1) &&
                 this.disableAttributeCheck(this.state.address2,    accountProps.address2) &&
                 this.disableAttributeCheck(this.state.city,        accountProps.city) &&
                 this.disableAttributeCheck(this.state.state,       accountProps.state) &&
                 this.disableAttributeCheck(this.state.country,     accountProps.country) &&
                 this.disableAttributeCheck(this.state.accountStatus, accountProps.accountStatus) &&
                 this.disableAttributeCheck(this.state.billingDayOfMonth, accountProps.billingDayOfMonth) &&
                 this.disableAttributeCheck(this.state.achActive,  accountProps.achActive) &&
                 this.disableAttributeCheck(this.state.useBudgets,  accountProps.useBudgets) &&
                 this.disableAttributeCheck(this.state.maxCharge, accountProps.maxCharge) &&
                 this.disableAttributeCheck(this.state.floatLimit, accountProps.floatLimit) &&
                 this.disableAttributeCheck(this.state.mccAuth,  accountProps.mccAuth) &&
                 this.disableAttributeCheck(this.state.mccWhitelist, accountProps.mccWhitelist) &&
                 this.disableAttributeCheck(this.state.plaidCheckPercAllow, accountProps.plaidCheckPercAllow) &&
                 this.disableAttributeCheck(this.state.achLimitNopeek, accountProps.achLimitNopeek) &&
                 this.disableAttributeCheck(this.state.achLimitAbsolute, accountProps.achLimitAbsolute) &&
                 this.disableAttributeCheck(this.state.achPercAvailableOnCharge, accountProps.achPercAvailableOnCharge) &&
                 this.disableAttributeCheck(this.state.achMinAvailableOnCharge, accountProps.achMinAvailableOnCharge) &&
                 this.disableAttributeCheck(this.state.ccPercAvailableOnCharge, accountProps.ccPercAvailableOnCharge) &&
                 this.disableAttributeCheck(this.state.ccMinAvailableOnCharge, accountProps.ccMinAvailableOnCharge) &&
                 this.disableAttributeCheck(this.state.feeMonthly, accountProps.feeMonthly) &&
                 this.disableAttributeCheck(this.state.feeCustPercentage, accountProps.feeCustPercentage) &&
                 this.disableAttributeCheck(this.state.feeSelfPercentage, accountProps.feeSelfPercentage) &&
                 this.disableAttributeCheck(this.state.postalCode,  accountProps.postalCode);

    return retval;
  }
  
  disableAttributeCheck(stateVar, propsVar)
  {
      if (stateVar !== null)
      {
          if (propsVar === null && stateVar === '')
              return true;
          
          if (propsVar !== stateVar)
              return false;
      }
      return true;
  }

  toggleCoverFees = () => {
    if (this.state.coverFees === true) {
      this.setState({coverFees: false});
    } else {
      this.setState({coverFees: true});
    }     
  };


  showSpinner() {
    return this.state.showSpinner;
  }

  showActivateSpinner() {
    return this.state.showActivateSpinner;
  }

  showSuspendSpinner() {
    return this.state.showSuspendSpinner;
  }

  setCountry    = (country)    => { this.setState({country:    country}); }  
  setRegion     = (region)     => { this.setState({state:      region}); }  
  setCity       = (city)       => { this.setState({city:       city}); }  
  setPostalCode = (postalCode) => { this.setState({postalCode: postalCode}); }  
  setAddress1   = (address1)   => { this.setState({address1:   address1}); }  
  setAddress2   = (address2)   => { this.setState({address2:   address2}); }  

  componentDidMount() {
    const { isAuthenticatedAppBind } = this.props.auth;

    if (isAuthenticatedAppBind()) {
      const params = new URLSearchParams(this.props.location.search);
      let acctId = params.get('accountId');
   
      AccountLedgerSummaryFetch.fetch(acctId).toPromise()
      .then(aef => {
        this.setState({
          alsAssetBalance: aef.getaccountledgersummary.assetBalance,
          alsPayInAr: aef.getaccountledgersummary.payInAr,
          alsAssetAndFullArBalance: aef.getaccountledgersummary.assetAndFullArBalance,
          alsPayInArAvailable: aef.getaccountledgersummary.payInArAvailable,
          alsAssetAndArBalance: aef.getaccountledgersummary.assetAndArBalance,
          alsAssetArAuthBalance: aef.getaccountledgersummary.assetArAuthBalance,
          alsBalanceForTopupCalc: aef.getaccountledgersummary.alsBalanceForTopupCalc,
          alsLiabilityBalance: aef.getaccountledgersummary.liabilityBalance,
          alsEquityBalance: aef.getaccountledgersummary.equityBalance,
          alsMaxPayoutBalance: aef.getaccountledgersummary.maxPayoutBalance,
          alsAuthBalance: aef.getaccountledgersummary.authBalance,
          alsFloatBalanceDisplay: aef.getaccountledgersummary.alsFloatBalanceDisplay,
          alsFloatBalanceForAuth: aef.getaccountledgersummary.alsFloatBalanceForAuth
        });
      })
      .catch(err => {
        //nothing to do....
      });

      this.getPaymentRailsRecipientStatus();

    }
  }
 
  getPaymentRailsRecipientStatus() {

    const { callPartnerApi, logError } = this.props.auth;

    const params = new URLSearchParams(this.props.location.search);
    let acctId = params.get('accountId');


    let aPjwt = abEnvironment.getPjwt();
    
    let payload = {
      operation: "getPaymentRailsRecipient",
      params: {
        pJwt: aPjwt,
        accountId: acctId
      }
    };

    callPartnerApi('appbind/generic',
                    'POST',
                    payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      if (response.abMessage) {
        throw new Error('problem calling PR API')
      }
      if (response[0] && response[0].status) {
        this.setState({paymentRailsStatus: response[0].status});
      } else if (response.length === 0) {
        //have not even stated setting up a payment rails payout account
        this.setState({paymentRailsStatus: 'notsetup'});
      }
    }).catch(error => {
      this.setState({paymentRailsStatus: 'nocontact'});
      logError('Exception in PaymentRailsPayout.js: ' + error);
    });
  }

  getCurrencyDisplayValue(v) {
    let value = 0;
    if (v) {
      value = v;
    }
    let displayValue;
    try {
      displayValue = Number(value).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
    } catch (err) {
      displayValue = Number(0).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
    }

    return displayValue;
  }

  render() {
    const { isAuthenticated, isAuthenticatedAppBind, isAdminUser, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;
    const { value } = this.state;
    const statusStop   = red[300];
    const statusWaiting = amber[400];

    // Experiences for different personas...
    if (!isAuthenticated() || needAssent()) {
      return (
        <Redirect to='/home' />
      );
    } else if (isAuthenticatedAppBind()) {
      const params = new URLSearchParams(this.props.location.search);
      let acctId = params.get('accountId');

      return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={ResellerLookupQuery}
          variables={{accountId: acctId}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              let fp = params.get('fp');
              let backTo = "Accounts";
              if (fp != null && fp !== '') {
                backTo = fp;
              }
              if (props.accountTByAccountId === null)
              {
                return (
                  <PageTitle title="Reseller not found" showBackButton={true} name={backTo} history={this.props.history}/>
                );
              }

              let uid = abEnvironment.getUserAttribute('u_id');  
              let stripeCid = props.accountTByAccountId.stripeCid;
              let dwollaCid = props.accountTByAccountId.dwollaCid;
              let activecampaignId = props.accountTByAccountId.activecampaignId;
              let accountType = props.accountTByAccountId.accountType;

              let psIds = [];
              for (var i=0; i<props.accountTByAccountId.paymentSourceTsByAccountId.nodes.length; i++)
              {
                let psId = props.accountTByAccountId.paymentSourceTsByAccountId.nodes[i].psId;
                psIds.push(psId);
              }

              // Enables adding an app on a reseller account.  Not sure if this is relevant
              //let custAppLink = "customerappcreate?accountId=" + props.accountTByAccountId.accountId;
              let newUserLink = "usercreate?accountId=" + props.accountTByAccountId.accountId;
              let viewLedgerLink = "accountledgers?accountId=" + props.accountTByAccountId.accountId +
                                   "&accountName=" + props.accountTByAccountId.accountName;

              let showAddPrimaryUser = false;
              if (props.accountTByAccountId.userTsByAccountId.totalCount === 0) {
                showAddPrimaryUser = true;
              }

              let address = {
                country:    this.getValueForRender(this.state.country,    props.accountTByAccountId.country,    ''),
                state:      this.getValueForRender(this.state.state,      props.accountTByAccountId.state,      ''),
                city:       this.getValueForRender(this.state.city,       props.accountTByAccountId.city,       ''),
                postalCode: this.getValueForRender(this.state.postalCode, props.accountTByAccountId.postalCode, ''),
                address1:   this.getValueForRender(this.state.address1,   props.accountTByAccountId.address1,   ''),
                address2:   this.getValueForRender(this.state.address2,   props.accountTByAccountId.address2,   '')
              };

              let regionLabel = "Region";

              // For passing to funding source component
              let uInfo = {
                accountId:   props.accountTByAccountId.accountId,
                accountName: props.accountTByAccountId.accountName,
                description: props.accountTByAccountId.description,
                firstName:   props.accountTByAccountId.contactFirstName,
                lastName:    props.accountTByAccountId.contactLastName,
                email:       props.accountTByAccountId.contactEmail,
                phone:       props.accountTByAccountId.contactPhone,
                address1:    props.accountTByAccountId.address1,
                address2:    props.accountTByAccountId.address2,
                city:        props.accountTByAccountId.city,
                state:       props.accountTByAccountId.state,
                country:     props.accountTByAccountId.country,
                postalCode:  props.accountTByAccountId.postalCode,
                dwollaCid:   dwollaCid,
                stripeCid:   stripeCid,
                achActive:   props.accountTByAccountId.achActive
              };

              let planOverrides = false;
              if (props.accountTByAccountId.feeMonthly !== null || props.accountTByAccountId.feeCustPercentage !== null || props.accountTByAccountId.feeSelfPercentage !== null) {
                planOverrides = true;
              }

              //Does the reseller have a wallet?
              let hasWallet = false;
              if (props.accountTByAccountId.walletTsByAccountId.totalCount > 0) {
                hasWallet = true;
              }

              let pTitle = "Reseller: " + this.getValueForRender(this.state.accountName, props.accountTByAccountId.accountName, '');
              
              return (
                <Grid container spacing={3} direction="row" justifyContent="flex-start">
                  <Grid item xs={12}>
                    <PageTitle title={pTitle} showBackButton={true} name={backTo} noMargin={true} history={this.props.history}/>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={0} direction="row" justifyContent="space-around">
                        <Grid item xs={12}>
                          <Tabs
                            value={value}
                            onChange={this.handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                            scrollButtons="auto"
                            classes={{root: classes.tabroot}}
                          >
                            <Tab label="People" icon={<Contacts />} />
                            <Tab label="Funding" icon={<CreditCard />} />
                            <Tab label="Apps" icon={<Apps />} />
                            <Tab label="Invoices" icon={<MonetizationOn />} />
                          </Tabs>
                        </Grid>
                        <Grid item xs={12}>
                          {value === 0 &&
                            <TabContainer>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Typography variant="h6" style={AppBindStyles.sectionTitleFirst}>
                                    Account
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    id="customer-name" label="Reseller (business) name" 
                                    value={ this.getValueForRender(this.state.accountName, 
                                            props.accountTByAccountId.accountName,
                                            '') }
                                    onChange={ (e) => this.setState({ accountName: e.target.value }) }
                                    inputProps={{ maxLength: 64 }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    id="account-num" label="Account number" 
                                    value={ this.getValueForRender(this.state.accountNum, 
                                            props.accountTByAccountId.accountNum,
                                            '') }
                                    onChange={ (e) => this.setState({ accountNum: e.target.value }) }
                                    inputProps={{ maxLength: 64 }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    id="description" label="Description"
                                    value={ this.getValueForRender(this.state.description, 
                                            props.accountTByAccountId.description,
                                            '') }
                                    multiline
                                    onChange={ (e) => this.setState({ description: e.target.value }) }
                                    inputProps={{ maxLength: 1024 }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant='h6'>Admin actions</Typography>
                                </Grid>
                                { isAdminUser()  && !this.showActivateSpinner() && (props.accountTByAccountId.accountStatus === 2) &&
                                <Grid item>
                                  <Tooltip title='Click here to manually activate reseller and bypass verification'>
                                    <Button 
                                      color="primary"
                                      variant="outlined"
                                      className={classes.button}
                                      onClick={() => {this._activateReseller(acctId);}}>
                                      Activate
                                    </Button>
                                  </Tooltip>
                                </Grid>}
                                { isAdminUser() && this.showActivateSpinner() && (props.accountTByAccountId.accountStatus === 2) &&
                                <Grid item>
                                  <Button disabled={false} color="primary" variant="outlined" >
                                    <SavingSpinner/>
                                  </Button>
                                </Grid>}
                                { isAdminUser()  && !this.showSuspendSpinner() && (props.accountTByAccountId.accountStatus === 2) &&
                                <Grid item>
                                  <Tooltip title='Click here to manually suspend reseller and bypass verification'>
                                    <Button 
                                      color="primary"
                                      variant="outlined"
                                      className={classes.button}
                                      onClick={() => {this._suspendReseller(acctId);}}>
                                      Suspend
                                    </Button>
                                  </Tooltip>
                                </Grid>}
                                { isAdminUser() && this.showSuspendSpinner() && (props.accountTByAccountId.accountStatus === 2) &&
                                <Grid item>
                                  <Button disabled={false} color="primary" variant="outlined" >
                                    <SavingSpinner/>
                                  </Button>
                                </Grid>}

                                <Grid item style={{minWidth: 120}}>
                                  <FormControl variant="standard">
                                    <InputLabel shrink htmlFor="accountStatus">Account status</InputLabel>
                                    <Select
                                      value={this.getValueForRender(this.state.accountStatus, props.accountTByAccountId.accountStatus, 0)}
                                      inputProps={{
                                        name: 'accountStatus',
                                        id: 'accountStatus',
                                      }}
                                      onChange={(e) => { this.setState({ accountStatus: e.target.value }); } }
                                      disabled={props.accountTByAccountId.accountStatus === 2}
                                      className={classes.nselect}
                                    >
                                      <MenuItem value={0}>Active</MenuItem>
                                      <MenuItem value={1}>Suspended</MenuItem>
                                      <MenuItem value={2}>Pending</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item style={{minWidth: 130}}>
                                  <FormControl variant="standard" style={{'width': '180'}}>
                                    <InputLabel shrink htmlFor="billingDom">Billing day of month</InputLabel>
                                    <Select
                                      value={this.getValueForRender(this.state.billingDayOfMonth, props.accountTByAccountId.billingDayOfMonth, 31)}
                                      inputProps={{
                                        name: 'billingDom',
                                        id: 'billingDom',
                                      }}
                                      onChange={(e) => { this.setState({ billingDayOfMonth: e.target.value }); } }
                                      className={classes.nselect}
                                    >
                                      <MenuItem value={1}>1</MenuItem>
                                      <MenuItem value={2}>2</MenuItem>
                                      <MenuItem value={3}>3</MenuItem>
                                      <MenuItem value={4}>4</MenuItem>
                                      <MenuItem value={5}>5</MenuItem>
                                      <MenuItem value={6}>6</MenuItem>
                                      <MenuItem value={7}>7</MenuItem>
                                      <MenuItem value={8}>8</MenuItem>
                                      <MenuItem value={9}>9</MenuItem>
                                      <MenuItem value={10}>10</MenuItem>
                                      <MenuItem value={11}>11</MenuItem>
                                      <MenuItem value={12}>12</MenuItem>
                                      <MenuItem value={13}>13</MenuItem>
                                      <MenuItem value={14}>14</MenuItem>
                                      <MenuItem value={15}>15</MenuItem>
                                      <MenuItem value={16}>16</MenuItem>
                                      <MenuItem value={17}>17</MenuItem>
                                      <MenuItem value={18}>18</MenuItem>
                                      <MenuItem value={19}>19</MenuItem>
                                      <MenuItem value={20}>20</MenuItem>
                                      <MenuItem value={21}>21</MenuItem>
                                      <MenuItem value={22}>22</MenuItem>
                                      <MenuItem value={23}>23</MenuItem>
                                      <MenuItem value={24}>24</MenuItem>
                                      <MenuItem value={25}>25</MenuItem>
                                      <MenuItem value={26}>26</MenuItem>
                                      <MenuItem value={27}>27</MenuItem>
                                      <MenuItem value={28}>28</MenuItem>
                                      <MenuItem value={29}>29</MenuItem>
                                      <MenuItem value={30}>30</MenuItem>
                                      <MenuItem value={31}>31</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                {!hasWallet &&
                                <React.Fragment>
                                  <Grid item style={{width: 140}}>
                                    <CurrencyTextField
                                      id="maxCharge" label="Max charge"
                                      value={ this.getValueForRender(this.state.maxCharge, 
                                              props.accountTByAccountId.maxCharge,
                                              '') }
                                      onChange={ (e) => this.setState({ maxCharge: e.target.value }) }
                                      showErrMsg={true}
                                      allowNegative={false}
                                      minValue={0}
                                      minEnterValue={0}
                                      textAlign="right"
                                    />
                                  </Grid>
                                  <Grid item style={{width: 140}}>
                                    <CurrencyTextField 
                                      id="float-limit" label="Float limit"
                                      value={ this.getValueForRender(this.state.floatLimit, 
                                              props.accountTByAccountId.floatLimit,
                                              '') }
                                      onChange={ (e) => this.setState({ floatLimit: e.target.value }) }
                                      showErrMsg={true}
                                      allowNegative={false}
                                      minValue={0}
                                      minEnterValue={0}
                                      textAlign="right"
                                    />
                                  </Grid>
                                </React.Fragment>
                                }
                                { isAdminUser() && this.state.alsAssetBalance != null &&
                                <Grid item xs={12}>
                                  <Grid container spacing={2} justifyContent="flex-start">
                                    <Grid item xs={12}>
                                      <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-end">
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Typography align="center" variant="h6">Balance</Typography>
                                              </Grid>
                                              <Grid item>
                                                <Tooltip placement='bottom' title="Total equity - what we'll show as the balance for the account">
                                                  <Typography align="center" variant="body1">Equity balance</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsEquityBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title='Asset balance for the account - generally usable funds'>
                                                  <Typography align="center" variant="body1">Asset</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsAssetBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title="Total liabilities - typically what is owed to AppBind, which should only occur when we provide a float.">
                                                  <Typography align="center" variant="body1">Liability</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsLiabilityBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title="This is the total virtual card charge amount that we've authorized but hasn't cleared yet.  We don't bill until the auth clears, as it could be reversed.">
                                                  <Typography align="center" variant="body1">Auths</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsAuthBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title="These are inbound payments that haven't arrived yet (e.g. ACH top-up that hasn't cleared yet)">
                                                  <Typography align="center" variant="body1">Topup ttl</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsPayInAr)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title="This is the portion of inbound payments that haven't arrived yet that we've credited to the account for used in subscription purchases.">
                                                  <Typography align="center" variant="body1">Topup avl</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsPayInArAvailable)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Typography align="center" variant="h6">Available</Typography>
                                              </Grid>
                                              <Grid item>
                                                <Tooltip placement='bottom' title="Previous amount less any outstanding auths.  This is the amount that is shown to a reseller as available balance.">
                                                  <Typography align="center" variant="body1">Available balance</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsAssetAndArBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Typography align="center" variant="h6">Max Payout</Typography>
                                              </Grid>
                                              <Grid item>
                                                <Tooltip placement='bottom' title="The maximum amount we'll allow to be paid out.  This is asset less liabilities.">
                                                  <Typography align="center" variant="body1">Maximum payout</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsMaxPayoutBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Typography align="center" variant="h6">Float</Typography>
                                              </Grid>
                                              <Grid item>
                                                <Tooltip placement='bottom' title="The amount we're currently floating to this account.">
                                                  <Typography align="center" variant="body1">Float balance</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsFloatBalanceDisplay)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        { isAdminUser() &&
                                        <Grid item>
                                          <Button 
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => {this.goTo(viewLedgerLink);}}>
                                            View ledgers
                                          </Button>
                                        </Grid>}
                                        <Grid item>
                                          <Button 
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => {this.goTo( 'featureflagmanage?accountId=' + props.accountTByAccountId.accountId);}}>
                                            Edit Feature Flags
                                          </Button>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                    <Grid item>
                                      <CurrencyTextField 
                                        id="payout" label="Payout"
                                        value={ this.state.payout }
                                        onChange={ (e) => this.setState({ payout: e.target.value }) }
                                        showErrMsg={true}
                                        allowNegative={false}
                                        minValue={0}
                                        minEnterValue={0}
                                        textAlign="right"
                                      />
                                    </Grid>
                                    <Grid item>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={(this.state.coverFees === true)}
                                            onChange={() => {this.toggleCoverFees()} }
                                            color="primary"
                                          />
                                        }
                                        label="AppBind cover fees"
                                      />
                                    </Grid>
                                    <Grid item>
                                      <SaveButton
                                        color="secondary"
                                        variant="outlined"
                                        label="Process payout"
                                        snackbarOpen={this.state.saveSnackbarOpen}
                                        autoHideDuration={this.state.saveSnackbarDuration}
                                        onSaveClick={() => { this.initiatePayout(acctId); }}
                                        onSnackbarClose={() => { this._onSnackbarClose(); }}
                                        status={this.state.saveStatus}
                                        message={this.state.saveMessage}
                                        disabled={this.disablePayoutButton()}/>
                                    </Grid>
                                    {this.state.paymentRailsStatus === 'incomplete' &&
                                      <Grid item>
                                        <Tooltip placement='bottom' title={'Reseller incomplete in Payment Rails.  Please go to Payment Rails dashboard and review.'}>
                                          <HourglassEmpty htmlColor={statusWaiting} />
                                        </Tooltip>
                                      </Grid>
                                    }
                                    {this.state.paymentRailsStatus === 'notsetup' &&
                                      <Grid item>
                                        <Tooltip placement='bottom' title={'Reseller has not attempted to set up a payment rails account'}>
                                          <HourglassEmpty htmlColor={statusStop} />
                                        </Tooltip>
                                      </Grid>
                                    }
                                    {this.state.paymentRailsStatus === 'nocontact' &&
                                      <Grid item>
                                        <Tooltip placement='bottom' title={'Payment Rails api issue.  Cannot verify status.  Please confirm proper status in payment rails.'}>
                                          <HourglassEmpty htmlColor={statusStop} />
                                        </Tooltip>
                                      </Grid>
                                    }
                                  </Grid>
                                </Grid>}

                                { isAdminUser() &&
                                <Grid item xs={12}>
                                  <Grid container spacing={2} justifyContent="flex-start">
                                    <Grid item>
                                      <CurrencyTextField 
                                        id="acctcredit" label="Account credit"
                                        value={ this.state.acctCredit }
                                        onChange={ (e) => {
                                          const valueChange = Number(e.target.value);
                                          
                                          let stateToChange = {
                                            acctCredit: e.target.value
                                          };
                                          
                                          //if the default note is the same as start ('Account credit' or 'Account debit') and this value shifts, change.
                                          let creditNotes = this.state.creditNotes;
                                          if (valueChange > 0 && creditNotes === 'Account debit') {
                                            stateToChange.creditNotes = 'Account credit';
                                          } else if (valueChange < 0 && creditNotes === 'Account credit') {
                                            stateToChange.creditNotes = 'Account debit';
                                          }
                                          
                                          this.setState(stateToChange);
                                        }}
                                        showErrMsg={true}
                                        allowNegative={true}
                                        textAlign="right"
                                      />
                                    </Grid>
                                    <Grid item>
                                      <TextField
                                        id="notes" label="Notes"
                                        value={ this.state.creditNotes }
                                        onChange={ (e) => this.setState({ creditNotes: e.target.value }) }
                                      />
                                    </Grid>
                                    <Grid item>
                                      <SaveButton
                                        color="secondary"
                                        variant="outlined"
                                        label="Issue credit"
                                        snackbarOpen={this.state.saveSnackbarOpen}
                                        autoHideDuration={this.state.saveSnackbarDuration}
                                        onSaveClick={() => { this.initiateCredit(acctId); }}
                                        onSnackbarClose={() => { this._onSnackbarClose(); }}
                                        status={this.state.saveStatus}
                                        message={this.state.saveMessage}
                                        disabled={this.disableCreditButton()}/>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                }

                                <Grid item xs={3}>
                                  <FormControl variant="standard"
                                    className={classes.formControl}
                                    style={{'width': '97%'}}
                                  >
                                    <InputLabel shrink htmlFor="use_budgets">Budget feature</InputLabel>
                                    <Select
                                      value={this.getValueForRender(this.state.useBudgets, props.accountTByAccountId.useBudgets, 0)}
                                      inputProps={{
                                        name: 'useBudgets',
                                        id: 'useBudgets',
                                      }}
                                      onChange={(e) => { this.setState({ useBudgets: e.target.value }); } }
                                      className={classes.nselect}
                                    >
                                      <MenuItem value={0}>Do not use budgets</MenuItem>
                                      <MenuItem value={1}>Use budgets</MenuItem>
                                      <MenuItem value={2}>Require budgets</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={3}>
                                  <FormControl variant="standard"
                                    className={classes.formControl}
                                    style={{'width': '97%'}}
                                  >
                                    <InputLabel shrink htmlFor="ach">ACH</InputLabel>
                                    <Select
                                      value={this.getValueForRender(this.state.achActive, props.accountTByAccountId.achActive, 0)}
                                      inputProps={{
                                        name: 'accountStatus',
                                        id: 'accountStatus',
                                      }}
                                      onChange={(e) => { this.setState({ achActive: e.target.value }); } }
                                      className={classes.nselect}
                                    >
                                      <MenuItem value={0}>Inactive</MenuItem>
                                      <MenuItem value={1}>Active</MenuItem>
                                      <MenuItem value={2}>Enable KYB</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={6}/>

                                <Grid item xs={6}>
                                      <TextField
                                        id="MCCWhitelist" label="MCC Whitelist (comma separated, no spaces)"
                                        value={ this.getValueForRender(this.state.mccWhitelist, props.accountTByAccountId.mccWhitelist, '') }
                                        onChange={ (e) => this.setState({ mccWhitelist: e.target.value }) }
                                        inputProps={{ maxLength: 2048 }}
                                      />
                                </Grid>
                                
                                <Grid item xs={3}>
                                  <FormControl variant="standard"
                                    className={classes.formControl}
                                    style={{'width': '97%'}}
                                  >
                                    <InputLabel shrink htmlFor="use_budgets">MCC Authorization</InputLabel>
                                    <Select
                                      value={this.getValueForRender(this.state.mccAuth, props.accountTByAccountId.mccAuth, 0)}
                                      inputProps={{
                                        name: 'mccAuth',
                                        id: 'mccAuth',
                                      }}
                                      onChange={(e) => { this.setState({ mccAuth: e.target.value }); } }
                                      className={classes.nselect}
                                    >
                                      <MenuItem value={0}>Whitelist only</MenuItem>
                                      <MenuItem value={1}>Allow blacklist</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={3}/>

                                <Grid item xs={12}>
                                  <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                    {'Fraud protection'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                  <PercentageTextField 
                                    id="plaid-balance % allow"
                                    label="Plaid balance % allow"
                                    className={classes.percentField}
                                    value={this.getValueForRender(this.state.plaidCheckPercAllow, props.accountTByAccountId.plaidCheckPercAllow, '') }
                                    showErrMsg={true}
                                    allowNegative={false}
                                    minValue={0}
                                    maxValue={100}
                                    minEnterValue={-9999}
                                    maxEnterValue={9999}
                                    onChange={ (e) => { this.setState({ plaidCheckPercAllow: e.target.value }); } }
                                    textAlign="right"
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <CurrencyTextField 
                                    id="achLimitNoPeek" label="ACH limit no peek"
                                    value={ this.getValueForRender(this.state.achLimitNopeek, 
                                            props.accountTByAccountId.achLimitNopeek,
                                            '') }
                                    onChange={ (e) => this.setState({ achLimitNopeek: e.target.value }) }
                                    showErrMsg={true}
                                    allowNegative={false}
                                    minValue={0}
                                    minEnterValue={0}
                                    textAlign="right"
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <CurrencyTextField 
                                    id="achLimitAsolute" label="ACH limit absolute"
                                    value={ this.getValueForRender(this.state.achLimitAbsolute, 
                                            props.accountTByAccountId.achLimitAbsolute,
                                            '') }
                                    onChange={ (e) => this.setState({ achLimitAbsolute: e.target.value }) }
                                    showErrMsg={true}
                                    allowNegative={false}
                                    minValue={0}
                                    minEnterValue={0}
                                    textAlign="right"
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <PercentageTextField 
                                    id="ach-%-allow-charge"
                                    label="ACH % available on charge"
                                    className={classes.percentField}
                                    value={this.getValueForRender(this.state.achPercAvailableOnCharge, props.accountTByAccountId.achPercAvailableOnCharge, '') }
                                    showErrMsg={true}
                                    allowNegative={false}
                                    minValue={0}
                                    maxValue={100}
                                    minEnterValue={-9999}
                                    maxEnterValue={9999}
                                    onChange={ (e) => { this.setState({ achPercAvailableOnCharge: e.target.value }); } }
                                    textAlign="right"
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <CurrencyTextField 
                                    id="achMinAvailableOnCharge" label="ACH min available on charge"
                                    value={ this.getValueForRender(this.state.achMinAvailableOnCharge, 
                                            props.accountTByAccountId.achMinAvailableOnCharge,
                                            '') }
                                    onChange={ (e) => this.setState({ achMinAvailableOnCharge: e.target.value }) }
                                    showErrMsg={true}
                                    allowNegative={false}
                                    minValue={0}
                                    maxValue={5000}
                                    minEnterValue={-9999}
                                    maxEnterValue={9999}
                                    textAlign="right"
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <PercentageTextField 
                                    id="ccPercAvailableOnCharge"
                                    label="CC % available on charge"
                                    className={classes.percentField}
                                    value={this.getValueForRender(this.state.ccPercAvailableOnCharge, props.accountTByAccountId.ccPercAvailableOnCharge, '') }
                                    showErrMsg={true}
                                    allowNegative={false}
                                    minValue={0}
                                    maxValue={100}
                                    minEnterValue={-9999}
                                    maxEnterValue={9999}
                                    onChange={ (e) => { this.setState({ ccPercAvailableOnCharge: e.target.value }); } }
                                    textAlign="right"
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <CurrencyTextField 
                                    id="ccMinAvailableOnCharge" label="CC min available on charge"
                                    value={ this.getValueForRender(this.state.ccMinAvailableOnCharge, 
                                            props.accountTByAccountId.ccMinAvailableOnCharge,
                                            '') }
                                    onChange={ (e) => this.setState({ ccMinAvailableOnCharge: e.target.value }) }
                                    showErrMsg={true}
                                    allowNegative={false}
                                    minValue={0}
                                    maxValue={5000}
                                    minEnterValue={-9999}
                                    maxEnterValue={9999}
                                    textAlign="right"
                                  />
                                </Grid>    
                                <Grid item xs={12}>
                                  <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                    {'Plan details: ' + props.accountTByAccountId.feePlanTByFeePlanId.feePlanName}
                                  </Typography>
                                </Grid>


                                <Grid item xs={12}>
                                  <Grid container justifyContent="space-between">
                                    <Grid item xs={3}>
                                      <CurrencyTextField 
                                        id="maxCharge" label="Plan Monthly fee"  className={classes.monthlyFeeTextField}
                                        value={ props.accountTByAccountId.feePlanTByFeePlanId.feeMonthly }
                                        disabled={true}
                                        textAlign="right"
                                      />
                                    </Grid>
                                    <Grid item  xs={3}>
                                      <PercentageTextField label="Plan Customer % fee"
                                        id="customer-percentage-fee" className={classes.percentageTextField}
                                        value={ props.accountTByAccountId.feePlanTByFeePlanId.feeCustPercentage}
                                        disabled={true}
                                        textAlign="right"/>
                                    </Grid>
                                    <Grid item  xs={3}>
                                      <PercentageTextField label="Plan Reseller % fee"
                                        id="customer-percentage-fee" className={classes.percentageTextField}
                                        value={props.accountTByAccountId.feePlanTByFeePlanId.feeSelfPercentage}
                                        disabled={true}
                                        textAlign="right"/>
                                    </Grid>
                                    <Grid item  xs={3}>
                                    
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <Grid container justifyContent="space-between">
                                    <Grid item  xs={3}>
                                      <CurrencyTextField 
                                        id="maxCharge" label="Override monthly fee"  className={classes.monthlyFeeTextField}
                                        value={ this.getValueForRender(this.state.feeMonthly, props.accountTByAccountId.feeMonthly, null) }
                                        onChange={ (e) => {this.setFeeMonthly(e.target.value) } }
                                        showErrMsg={true}
                                        allowNegative={false}
                                        minValue={0}
                                        minEnterValue={0}
                                        textAlign="right"
                                      />
                                    </Grid>
                                    <Grid item xs={3}>
                                      <NullablePercentageTextField label="Override customer % fee"
                                        id="customer-percentage-fee" className={classes.percentageTextField}
                                        value={ this.getValueForRender(this.state.feeCustPercentage, props.accountTByAccountId.feeCustPercentage, null)}
                                        showErrMsg={true}
                                        allowNegative={false}
                                        minValue={0}
                                        maxValue={100}
                                        minEnterValue={0}
                                        maxEnterValue={5}
                                        onChange={(e) => {this.setFeeCustPercentage(e.target.value, props) } }
                                        textAlign="right"/>
                                    </Grid>
                                    <Grid item xs={3}>
                                      <NullablePercentageTextField label="Override reseller % fee"
                                        id="customer-percentage-fee" className={classes.percentageTextField}
                                        value={this.getValueForRender(this.state.feeSelfPercentage, props.accountTByAccountId.feeSelfPercentage, null)}
                                        showErrMsg={true}
                                        allowNegative={false}
                                        minValue={0}
                                        maxValue={100}
                                        minEnterValue={0}
                                        maxEnterValue={5}
                                        onChange={(e) => {this.setFeeSelfPercentage(e.target.value, props) } }
                                        textAlign="right"/>
                                    </Grid>
                                    <Grid item xs={3}>
                                      <Button 
                                        color="primary"
                                        variant="outlined"
                                        disabled={!planOverrides}
                                        className={classes.button}
                                        onClick={() => {this._resetResellerToPlan(acctId);}}>
                                        Clear overrides
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                  <Grid container justifyContent="space-between">
                                    <Grid item>
                                      <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                        Primary contact
                                      </Typography>
                                    </Grid>
                                    { showAddPrimaryUser &&
                                    <Grid item>
                                      <Tooltip title="Add a reseller user for the primary account contact">
                                        <Button 
                                          color="secondary"
                                          variant="outlined"
                                          className={classes.button}
                                          onClick={() => {this.goToUserCreate(newUserLink, props.accountTByAccountId);}}>
                                          <Add/> Add user for primary contact
                                        </Button>
                                      </Tooltip>
                                    </Grid>
                                    }

                                    { this.showSpinner() && accountType === 3 &&
                                      <Grid item>
                                        <Button disabled={false} color="primary" variant="outlined" >
                                          <SavingSpinner/>
                                        </Button>
                                      </Grid>
                                    }
                                    { activecampaignId == null && accountType === 3 && !this.showSpinner() &&
                                      <Grid item>
                                        <Button 
                                          color="primary"
                                          variant="outlined"
                                          className={classes.button}
                                          onClick={() => { this._activecampaignSync(acctId, accountType); }}
                                        >
                                          Link to ActiveCampaign
                                        </Button>
                                      </Grid>
                                    }
                                    { isAdminUser() && !this.state.showTopupSpinner &&
                                    <Grid item>
                                      <Button color="primary" variant="outlined"  className={classes.button} onClick={() => { this.checkAndProcessAutoTopup(acctId); }}>
                                        Check / Process Auto Topup
                                      </Button>
                                    </Grid>
                                    }
                                    { this.state.showTopupSpinner &&
                                      <Grid item>
                                        <Button disabled={false} color="primary" variant="outlined" className={classes.button}>
                                          <SavingSpinner/>
                                        </Button>
                                      </Grid>
                                    }

                                  </Grid>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField data-recording-ignore="mask"
                                    id="first-name" label="First name"
                                    value={ this.getValueForRender(this.state.firstName, 
                                            props.accountTByAccountId.contactFirstName,
                                            '') }
                                    onChange={ (e) => this.setState({ firstName: e.target.value }) }
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField data-recording-ignore="mask"
                                    id="last-name" label="Last name" 
                                    value={ this.getValueForRender(this.state.lastName, 
                                            props.accountTByAccountId.contactLastName,
                                            '') }
                                    onChange={ (e) => this.setState({ lastName: e.target.value }) }
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField data-recording-ignore="mask"
                                    id="email" label="Email"
                                    value={ this.getValueForRender(this.state.email, 
                                            props.accountTByAccountId.contactEmail,
                                            '') }
                                    onChange={ (e) => this.setState({ email: e.target.value }) }
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField data-recording-ignore="mask"
                                    id="phone" label="Phone" className={classes.formControl}
                                    value={ this.getValueForRender(this.state.phone, 
                                            props.accountTByAccountId.contactPhone,
                                            '') }
                                    onChange={ (e) => this.setState({ phone: e.target.value }) }
                                    InputProps={{
                                      inputComponent: PhoneNumberFormat,
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                    Mailing address
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Address
                                    address={address}
                                    regionLabel={regionLabel}
                                    setCountryCodeState={this.setCountry}
                                    setRegionCodeState={this.setRegion}
                                    setCityState={this.setCity}
                                    setPostalCodeState={this.setPostalCode}
                                    setAddress1State={this.setAddress1}
                                    setAddress2State={this.setAddress2}
                                  />
                                </Grid>
                              </Grid>

                              <Grid container spacing={2} direction="row" justifyContent="flex-start">
                                <Grid item xs={1}>
                                  <SaveButton
                                    snackbarOpen={this.state.saveSnackbarOpen}
                                    autoHideDuration={this.state.saveSnackbarDuration}
                                    onSaveClick={() => { this._updateReseller(acctId); }}
                                    onSnackbarClose={() => { this._onSnackbarClose(); }}
                                    status={this.state.saveStatus}
                                    message={this.state.saveMessage}
                                    disabled={this.disableAccountSave(props.accountTByAccountId)}/>
                                </Grid>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="h6" style={AppBindStyles.sectionTitleTable}>
                                  Users
                                </Typography>
                              </Grid>

                              <Paper className={classes.paper4}>
                                <Table>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Email</TableCell>
                                      <TableCell>Phone</TableCell>
                                      <TableCell>
                                        <Tooltip title="Add a reseller user">
                                          <Button
                                            className={classes.iconbutton}
                                            variant="outlined"
                                            onClick={this.goToUserCreateNonPrimary.bind(this, newUserLink, props.accountTByAccountId)}>
                                            <Add className={classes.addicon}/>&nbsp;Invite&nbsp;user
                                          </Button>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell/>
                                      <TableCell/>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {props.accountTByAccountId.userTsByAccountId.nodes.map(n => {
                                      let tlink = 'useredit?userId=' + n.userId 
                                                + '&acctType=' + props.accountTByAccountId.accountType
                                                + '&acctId=' + props.accountTByAccountId.accountId;
                                      return (
                                        <TableRow key={n.userId}>
                                          <TableCell data-recording-sensitive>{n.firstName} {n.lastName}</TableCell>
                                          <TableCell data-recording-sensitive>{n.email}</TableCell>
                                          <TableCell data-recording-sensitive>{n.phone}</TableCell>
                                          <TableCell>
                                            { uid !== n.userId &&
                                            <Button className={classes.iconbutton} onClick={this.goTo.bind(this, tlink)}>
                                              <Edit className={classes.editicon}/>
                                            </Button>
                                            }
                                          </TableCell>
                                          <TableCell>
                                            {n.emailVerified === 0 &&
                                            <Button
                                              className={classes.vbutton}
                                              variant="outlined"
                                              onClick={this.confirmVerify.bind(this, n.userId)}>
                                              Resend Verify
                                            </Button>
                                            }
  
                                          </TableCell>
                                          <TableCell>
                                            <Button
                                              className={classes.mbutton}
                                              variant="outlined"
                                              onClick={this.masqueradeAs.bind(this, n.userId, acctId, 'appbind_reseller', n.userName)}>
                                              Masquerade
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Paper>
                            </TabContainer>
                          }
                          {value === 1 &&
                            <TabContainer {...this.props}>
                              <FundingSourceList
                                cp="reseller"
                                accountId={props.accountTByAccountId.accountId}
                                uInfo={uInfo}
                                auth={this.props.auth}
                                accountStatus={props.accountTByAccountId.accountStatus}
                              />
                            </TabContainer>
                          }
                          {value === 2 &&
                            <TabContainer>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>App name</TableCell>
                                    <TableCell>Markup / discount - fixed</TableCell>
                                    <TableCell>Markup / discount - percentage</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {props.accountTByAccountId.customerAppTsByCustomerAccountId.nodes.map(n => {
                                    let mabs = Number(n.appMarkupAbsolute).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

                                    return (
                                      <TableRow key={n.appId}>
                                        <TableCell>{n.appTByAppId.appName}</TableCell>
                                        <TableCell>{mabs}</TableCell>
                                        <TableCell>{n.appMarkupPercentage}%</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              {/*<Button color="primary" onClick={() => { this.goTo(custAppLink); }}>
                                Add App
                                </Button>*/}
                            </TabContainer>
                          }
                          {value === 3 &&
                            <TabContainer>
                              <Charges pAccountId={props.accountTByAccountId.accountId}
                                      auth={this.props.auth}/>
                          </TabContainer>
                          }
                        </Grid>
                      </Grid>
                    </Paper>
                    <ConfirmationDialog
                        open={this.state.verifyConfirm}
                        onClose={() => { this.setState({verifyConfirm:false, verifyUserId: null});}}
                        onConfirm={() => { this.resendVerifyEmail();}}
                        onCancel={() => { }}
                        title="Resend verification email?"
                        cancelBtn="No"
                        okBtn="Yes, send"
                      >
                      {'User will receive an verify email. Do you wish to proceed?'}
                    </ConfirmationDialog>
                  </Grid>
                </Grid>
              );
            }
            return <LoadingSpinner />
          }}
        />
      );
    } else {
        return (
          <Redirect to='/home' />
        );
    }    
  }

  _activecampaignSync(acctId, accountType) {
    const { callPartnerApi } = this.props.auth;

    this.setState({ showSpinner: true });

    let payload = {
      operation: "linkAccountToActiveCampaign",
      params: {
        accountId: acctId
      }
    }

    callPartnerApi('appbind/generic',
                     'POST',
                     payload
    )
    .then((response) => {
      return response.json();
    })
    .then((jresp) => {
      // Returned so that we can set on the account record.  But for now, we are
      // reloading the page at the end of the promise chain.
      let activecampaignId = jresp.activecampaignId;
      return activecampaignId;
    })
    .then(() => {
      this.setState({ showSpinner: false });      
      window.location.reload();
    })
    .catch(err => {
      this.setState({ showSpinner: false });
    });

  }

  checkAndProcessAutoTopup(acctId) {
    const { callPartnerApi } = this.props.auth;

    this.setState({ showTopupSpinner: true });

    let payload = {
      operation: "checkAndProcessAutoTopup",
      params: {
        accountId: acctId
      }
    }

    callPartnerApi('appbind/generic',
                     'POST',
                     payload
    )
    .then((response) => {
      return response.json();
    })
    .then(() => {
      this.setState({ showTopupSpinner: false });      
      window.location.reload();
    })
    .catch(err => {
      this.setState({ showTopupSpinner: false });
    });

  }


  _suspendReseller(acctId) {

    const { logError } = this.props.auth;
    this.setState({ showSuspendSpinner: true });
    
    AccountUpdateMutation.commit(acctId, null, null, null, 1, 
                                 null, null, null, null, 
                                 null, null, null, null, null, null,
                                 null, null, null, null, null, null, null, null, null, null)
    .then(response => {
      this.setState({ showSuspendSpinner: false });      
      window.location.reload();
    })
    .catch(error => {
      logError('Exception in reseller.js: ' + error);
      this.setState({ showSuspendSpinner: false });      
      window.location.reload();
    });

  }

  _activateReseller(acctId) {
    const { callPartnerApi } = this.props.auth;

    this.setState({ showActivateSpinner: true });

    let payload = {
      operation: "resellerStatusPendingToActive",
      params: {
        accountId: acctId
      }
    }

    callPartnerApi('appbind/generic',
                     'POST',
                     payload
    )
    .then((response) => {
      return response.json();
    })
    .then((jresp) => {
     return true;
    })
    .then(() => {
      this.setState({ showActivateSpinner: false });      
      window.location.reload();
    })
    .catch(err => {
      this.setState({ showActivateSpinner: false });
    });

  }

  _onSnackbarClose() {
    this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
  }

  _updateReseller(acctId) {
    this.setState({ saveStatus: 'saving' });
    const { accountName, accountNum, description, accountStatus, 
            lastName, firstName, email, phone,
            address1, address2, city, state, country, postalCode,
            billingDayOfMonth, maxCharge, floatLimit, achActive, useBudgets, mccAuth, mccWhitelist, feeMonthly, feeCustPercentage, feeSelfPercentage, 
            plaidCheckPercAllow, achLimitNopeek, achLimitAbsolute,  achPercAvailableOnCharge, achMinAvailableOnCharge, ccPercAvailableOnCharge, ccMinAvailableOnCharge} = this.state;
    
    AccountUpdateMutation.commit(acctId, accountName, accountNum, description, accountStatus, 
                                 lastName, firstName, email, phone, 
                                 address1, address2, city, state, country, postalCode,
                                 null, billingDayOfMonth, maxCharge, floatLimit, null, achActive, null, useBudgets, mccAuth, mccWhitelist,
                                 feeMonthly, feeCustPercentage, feeSelfPercentage, null, 
                                 plaidCheckPercAllow, achLimitNopeek, achLimitAbsolute,  achPercAvailableOnCharge, achMinAvailableOnCharge, 
                                 ccPercAvailableOnCharge, ccMinAvailableOnCharge)
                          .then(response => {
                            // console.log('Account updated')
                            this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Reseller saved.', saveSnackbarDuration: 3000 });
                          })
                          .catch(error => {
                            //console.log('Got error: ' + error);
                            this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Save reseller failed.', saveSnackbarDuration: null });
                          });
  }

  _resetResellerToPlan(acctId) {
    //this.setState({ saveStatus: 'saving ' });
    
    ResellerFeeUpdateMutation.commit(acctId, 
            null, null, null)
    .then(response => {
      // console.log('Account updated')
      //this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Reseller saved.', saveSnackbarDuration: 3000 });
      window.location.reload();
    })
    .catch(error => {
      //console.log('Got error: ' + error);
      //this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Save reseller failed.', saveSnackbarDuration: null });
    });
  }
}

Reseller.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(Reseller);
